import { useState } from 'react';
import { Alert, Button, Divider, Flex, Form, Input, Typography, theme } from 'antd';
import { useNavigate } from 'react-router';
import {
  ContinuarComGoogle,
  EtiquetaDaUltimaVez,
  formaDeEntradaAnterior,
  lembrarFormaDeEntrada,
  MENSAGEM_DE_FALHA_INESPERADA,
  mensagemDeErro,
  mensagemDoRetornoOAuth,
  MolduraDeAutenticacao,
  TituloDaTela,
} from '@titularis/ui';
import { authClient, signIn } from '../auth';

interface Campos {
  email: string;
  password: string;
}

/**
 * A entrada do backoffice.
 *
 * Mesma moldura e mesma ordem da tela do aluno - Google primeiro, linha do
 * "ou", formulário depois -, e por um motivo que não é estético: **é a mesma
 * identidade**. O cookie de sessão é compartilhado em `.titularis.com.br`, e
 * quem entra pelo Google no app já chega autenticado aqui. Duas telas
 * diferentes para a mesma conta leriam como dois produtos.
 *
 * Duas diferenças, e as duas são de conteúdo:
 *
 * - **não há "criar conta".** Acesso ao backoffice se concede, não se pede: o
 *   papel vem do banco e quem o atribui é o dono. Quem entrar sem papel
 *   administrativo é recusado pelo `RequireAdmin`, com explicação;
 * - **não há "esqueci minha senha"**, porque o link de redefinição volta para o
 *   app do aluno (`APP_URL/nova-senha`) e este domínio não tem essa rota. O
 *   caminho de volta de quem esqueceu a senha passou a ser o botão do Google,
 *   que é a mesma conta - e é uma saída melhor do que a que não existia.
 */
export function LoginPage() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  /*
   * A recusa do Google volta pelo endereço, e não por uma resposta: o fluxo
   * sai do domínio e a API devolve a pessoa aqui com `?error=...`.
   */
  const [erro, setErro] = useState<string | null>(() =>
    mensagemDoRetornoOAuth(new URLSearchParams(window.location.search).get('error')),
  );
  const [enviando, setEnviando] = useState(false);
  const [anterior] = useState(formaDeEntradaAnterior);

  const entrar = async ({ email, password }: Campos) => {
    setErro(null);
    setEnviando(true);

    try {
      const { error } = await signIn.email({ email, password });

      if (error) {
        setErro(mensagemDeErro(error, 'E-mail ou senha incorretos.'));
        return;
      }

      lembrarFormaDeEntrada('email');
      void navigate('/', { replace: true });
    } catch {
      setErro(MENSAGEM_DE_FALHA_INESPERADA);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <MolduraDeAutenticacao>
      <Flex vertical gap={token.margin}>
        {/*
          O título diz **onde** a pessoa está, e não "bem-vindo": o logotipo é o
          mesmo do app e os dois domínios só diferem por três letras na barra de
          endereço. Quem administra abre os dois no mesmo navegador.
        */}
        <TituloDaTela
          titulo="Backoffice do Titularis"
          descricao="Entre com sua conta do time para cadastrar, comentar e publicar."
        />

        {erro ? <Alert type="error" showIcon title={erro} /> : null}

        <ContinuarComGoogle
          authClient={authClient}
          destaque={anterior === 'google'}
          aoFalhar={(mensagem) => setErro(mensagem || null)}
        />

        <Divider plain style={{ margin: 0 }}>
          <Typography.Text type="secondary">ou</Typography.Text>
        </Divider>

        <Form<Campos> layout="vertical" onFinish={entrar} requiredMark={false} disabled={enviando}>
          <Form.Item
            name="email"
            label="E-mail"
            rules={[{ required: true, type: 'email', message: 'Informe um e-mail válido.' }]}
          >
            <Input
              autoComplete="email"
              inputMode="email"
              size="large"
              suffix={anterior === 'email' ? <EtiquetaDaUltimaVez /> : undefined}
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="Senha"
            rules={[{ required: true, message: 'Informe sua senha.' }]}
          >
            <Input.Password autoComplete="current-password" size="large" />
          </Form.Item>

          <Button type="primary" htmlType="submit" size="large" block loading={enviando}>
            Entrar
          </Button>
        </Form>

        {/* Não há cadastro aqui: acesso ao backoffice é concedido por um dono,
            nunca por auto-cadastro. */}
        <Typography.Text type="secondary" style={{ textAlign: 'center' }}>
          O acesso ao backoffice é concedido pelo responsável da plataforma.
        </Typography.Text>
      </Flex>
    </MolduraDeAutenticacao>
  );
}
